import GiftCardApiService from '@package/core/src/giftCard/GiftCardApiService';
import GiftCardInteractor from '@package/core/src/giftCard/GiftCardInteractor';
import { DeleteGiftCardRequest, GiftCardRequest } from '@package/core/src/giftCard/types';
import { ApiError } from '@package/services/src/ApiClient';

import { apiClient } from '../services';
import { CHEKOUT_SUMMARY_DATA_QUERY_KEY } from '../useCheckoutSummary/types';
import { useQueryClient, useStrictMutation, useStrictQuery } from '../useCustomQuery';
import { PAYMENT_METHODS_QUERY_KEY } from '../usePaymentMethods/types';
import { SHOPPING_CART_DATA_QUERY_KEY } from '../useShoppingCart/types';

import { GIFT_CARD_QUERY_KEY } from './types';

const giftCardService = new GiftCardApiService(apiClient);
const giftCardInteractor = new GiftCardInteractor(giftCardService);

const useGiftCard = () => {
  const queryClient = useQueryClient();

  const { data } = useStrictQuery(
    [GIFT_CARD_QUERY_KEY],
    async () => {
      const res = await giftCardInteractor.getGiftCards();

      if (res) return res.data;
    },
    { refetchOnWindowFocus: false, refetchOnMount: false },
  );

  const { mutate: deleteGiftCard } = useStrictMutation(
    async (data: DeleteGiftCardRequest) => await giftCardInteractor.deleteGiftCard(data),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([GIFT_CARD_QUERY_KEY]);
        void queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([CHEKOUT_SUMMARY_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([PAYMENT_METHODS_QUERY_KEY]);
      },
    },
  );

  const {
    data: giftCard,
    mutateAsync: applyGiftCard,
    error,
  } = useStrictMutation(
    async (data: GiftCardRequest) => {
      const res = await giftCardInteractor.applyGiftCard(data);

      return res.data;
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([GIFT_CARD_QUERY_KEY]);
        void queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([CHEKOUT_SUMMARY_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([PAYMENT_METHODS_QUERY_KEY]);
      },
    },
  );

  return {
    data,
    giftCard,
    error: error as ApiError | undefined,
    applyGiftCard,
    deleteGiftCard,
  };
};

export default useGiftCard;
